import { useState, type ReactNode } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment/moment";
import {
  useBandeauInformationAdmin,
  type BandeauInformationInput,
} from "../../hooks/use-bandeau-information";
import {
  BANDEAU_AUDIENCES,
  BANDEAU_AUDIENCE_LABELS,
  type BandeauInformation as BandeauInformationModel,
} from "../../models/bandeauInformation";
import BandeauInformation from "../UI/Layout/BandeauInformation";
import Card from "../UI/Card";
import Title from "../UI/Title";
import Text from "../UI/Text";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import Status from "../UI/Status";
import { Table } from "../UI/Table";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import TextArea from "../UI/TextArea";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import styles from "./BandeauInformationAdmin.module.css";

const validationSchema = Yup.object({
  titre: Yup.string()
    .max(255, "255 caractères maximum")
    .test(
      "titre-ou-texte",
      "Renseignez au moins un titre ou un texte",
      function (value) {
        return !!value?.trim() || !!this.parent.texte?.trim();
      },
    ),
  texte: Yup.string(),
  lien: Yup.string().matches(
    /^https?:\/\/.+/,
    "Le lien doit commencer par http:// ou https://",
  ),
  audiences: Yup.array()
    .of(Yup.string())
    .min(1, "Choisissez au moins un public"),
});

function formatAudiences(entry: BandeauInformationModel) {
  return BANDEAU_AUDIENCES.filter((audience) =>
    entry.audiences.includes(audience),
  )
    .map((audience) => BANDEAU_AUDIENCE_LABELS[audience])
    .join(", ");
}

function HistoryStatus({
  entry,
  isLatest,
}: {
  entry: BandeauInformationModel;
  isLatest: boolean;
}) {
  if (entry.retired_at) {
    return (
      <Status
        color="neutral"
        text={`Retiré le ${moment(entry.retired_at).format("DD/MM/YYYY")}`}
      />
    );
  }
  if (isLatest) {
    return <Status color="success" text="En ligne" />;
  }
  return <Status color="neutral" text="Remplacé" />;
}

export default function BandeauInformationAdmin() {
  const {
    history,
    current: bandeau,
    publish,
    retire,
  } = useBandeauInformationAdmin();
  const toast = useToast();
  const [error, setError] = useState<ReactNode>("");
  const [isRetiring, setIsRetiring] = useState(false);

  const initialValues: BandeauInformationInput = {
    titre: bandeau?.titre ?? "",
    texte: bandeau?.texte ?? "",
    lien: bandeau?.lien ?? "",
    lien_nouvel_onglet: bandeau?.lien_nouvel_onglet ?? true,
    audiences: bandeau?.audiences ?? BANDEAU_AUDIENCES,
  };

  async function submitHandler(values: BandeauInformationInput) {
    setError("");
    try {
      await publish(values);
      toast.success({
        title: "Bandeau publié",
        description: "Il s'affiche pour les publics choisis.",
      });
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  async function retireHandler() {
    setIsRetiring(true);
    try {
      await retire();
      toast.success({ title: "Bandeau retiré" });
    } catch (e) {
      toast.error({
        title: "Impossible de retirer le bandeau",
        description: handleAPIError(e),
      });
    } finally {
      setIsRetiring(false);
    }
  }

  return (
    <Card className={styles.card}>
      <Title variant="kilo">Bandeau d'information</Title>
      <Text>
        Le bandeau s'affiche en haut de l'application pour les publics choisis.
        Chaque publication crée un nouveau bandeau : les utilisateurs qui
        avaient fermé le précédent le voient réapparaître. Retirer le bandeau le
        masque sans effacer l'historique.
      </Text>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({ errors, touched }) => (
          <Form className={styles.form}>
            <TextInput
              type="text"
              label="Titre"
              name="titre"
              placeholder="Offre exclusive en partenariat avec Qalimo"
            />
            <TextArea
              label="Texte"
              name="texte"
              rows={3}
              placeholder="Simplifiez la gestion de vos locations…"
            />
            <TextInput
              type="text"
              label="Lien « Voir l'offre »"
              name="lien"
              placeholder="https://"
              subText="Sans lien, le bouton n'est pas affiché."
            />
            <Checkbox name="lien_nouvel_onglet">
              Ouvrir le lien dans un nouvel onglet (sinon sur la page actuelle)
            </Checkbox>
            <fieldset className={styles.audiences}>
              <Text as="legend" weight="medium">
                Diffuser à
              </Text>
              <div className={styles.audienceChoices}>
                {BANDEAU_AUDIENCES.map((audience) => (
                  <Checkbox key={audience} name="audiences" value={audience}>
                    {BANDEAU_AUDIENCE_LABELS[audience]}
                  </Checkbox>
                ))}
              </div>
              {touched.audiences && typeof errors.audiences === "string" && (
                <Text color="danger" variant="deci">
                  {errors.audiences}
                </Text>
              )}
            </fieldset>
            <Callout variant="danger">{error}</Callout>
            <div className={styles.actions}>
              {bandeau && (
                <Button
                  variant="danger"
                  type="button"
                  isLoading={isRetiring}
                  onClick={() => retireHandler()}
                >
                  Retirer le bandeau
                </Button>
              )}
              <FormikSubmitButton>Publier</FormikSubmitButton>
            </div>
          </Form>
        )}
      </Formik>

      {bandeau && (
        <div className={styles.section}>
          <Title variant="hecto">Bandeau en ligne</Title>
          <BandeauInformation bandeau={bandeau} />
        </div>
      )}

      <div className={styles.section}>
        <Title variant="hecto">Historique</Title>
        {history.length === 0 ? (
          <Text>Aucun bandeau publié pour l'instant.</Text>
        ) : (
          <Table expandable rowCount={history.length}>
            <Table.Header>
              <Table.Column>Publié le</Table.Column>
              <Table.Column>Titre</Table.Column>
              <Table.Column>Texte</Table.Column>
              <Table.Column>Lien</Table.Column>
              <Table.Column>Ouverture</Table.Column>
              <Table.Column>Diffusion</Table.Column>
              <Table.Column>Statut</Table.Column>
            </Table.Header>
            <Table.Body>
              {history.map((entry, index) => (
                <Table.Row key={entry.id}>
                  <Table.Cell>
                    <Text as="div">
                      {moment(entry.created_at).format("DD/MM/YYYY HH:mm")}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text as="div">{entry.titre}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text as="div">{entry.texte}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    {entry.lien && (
                      <Text
                        as="a"
                        href={entry.lien}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {entry.lien}
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Text as="div">
                      {entry.lien &&
                        (entry.lien_nouvel_onglet
                          ? "Nouvel onglet"
                          : "Même page")}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text as="div">{formatAudiences(entry)}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <HistoryStatus entry={entry} isLatest={index === 0} />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </Card>
  );
}
